/**
 * Getting the coefficient set, and keeping it out of everywhere it must not be.
 *
 * The fitted table is not in the bundle. It is fetched from the API Worker once
 * per session and held in a module-level variable — never `localStorage`, never
 * `sessionStorage`, never logged.
 *
 * ## The offline copy
 *
 * Offline You-vs-CPU still needs the numbers, so a copy goes into IndexedDB.
 * The plan calls that copy "encrypted at rest", and it is worth being honest
 * about what that means here: any key the client can use to decrypt, the client
 * also has to hold, so this is obfuscation, not cryptography. It stops the
 * table being readable at a glance in devtools' storage panel. It stops nothing
 * else, and it is not what makes cheating hard.
 *
 * What makes cheating hard is server replay: every competitive result is
 * re-derived on the Worker from `(seed, decisionLog, coefficientVersion)`, and
 * a mismatch is rejected. That is why this file spends no effort pretending to
 * be secure, and every effort making sure the version travels with the result.
 */

import { CURRENT_COEFFICIENT_VERSION, type Coefficients } from '@ipl-sim/engine-cricket';

const DB_NAME = 'p11';
const STORE = 'kv';
const KEY = 'c';

/**
 * The endpoint. Unset in development, where there is no Worker running — the
 * app then falls back to the offline copy, and failing that reports honestly
 * rather than silently simulating with numbers it invented.
 */
const endpoint = import.meta.env.VITE_COEFFICIENTS_ENDPOINT ?? '';

/** In memory for the session. Never persisted from here. */
let held: Coefficients | null = null;

// ── IndexedDB, minimally ─────────────────────────────────────────────────────

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * A reversible scramble, not encryption — see the note at the top of this file.
 * XOR against a key stretched from the version, so a stored blob is not
 * readable as JSON in the storage inspector.
 */
function scramble(text: string): string {
  const key = `p11.${CURRENT_COEFFICIENT_VERSION}.coefficients`;
  let out = '';
  for (let i = 0; i < text.length; i++) {
    out += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return btoa(out);
}

function unscramble(blob: string): string {
  const text = atob(blob);
  const key = `p11.${CURRENT_COEFFICIENT_VERSION}.coefficients`;
  let out = '';
  for (let i = 0; i < text.length; i++) {
    out += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return out;
}

async function readCached(): Promise<Coefficients | null> {
  try {
    const db = await openDb();
    const blob = await new Promise<unknown>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly').objectStore(STORE).get(KEY);
      tx.onsuccess = () => resolve(tx.result);
      tx.onerror = () => reject(tx.error);
    });
    if (typeof blob !== 'string') return null;
    const parsed = JSON.parse(unscramble(blob)) as Coefficients;
    // A cached set from an older deploy must not be used silently: the version
    // travels with every submitted result, and replaying against the wrong set
    // would read as a cheat rather than a stale cache.
    return parsed.version === CURRENT_COEFFICIENT_VERSION ? parsed : null;
  } catch {
    return null;
  }
}

async function writeCached(coefficients: Coefficients): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db
        .transaction(STORE, 'readwrite')
        .objectStore(STORE)
        .put(scramble(JSON.stringify(coefficients)), KEY);
      tx.onsuccess = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* an uncacheable set only costs offline play, never this match */
  }
}

// ── the one entry point ──────────────────────────────────────────────────────

export class CoefficientsUnavailable extends Error {
  constructor() {
    super('coefficients unavailable');
    this.name = 'CoefficientsUnavailable';
  }
}

/**
 * Network first, offline copy second, and an honest failure third.
 *
 * There is deliberately no built-in default to fall back on. Simulating with
 * invented numbers would produce a match that looks real, cannot be replayed by
 * the server, and would be indistinguishable from a tampered result.
 */
export async function getCoefficients(): Promise<Coefficients> {
  if (held) return held;

  if (endpoint) {
    try {
      const response = await fetch(endpoint, { cache: 'no-store' });
      if (response.ok) {
        const fetched = (await response.json()) as Coefficients;
        held = fetched;
        void writeCached(fetched);
        return fetched;
      }
    } catch {
      /* fall through to the offline copy */
    }
  }

  const cached = await readCached();
  if (cached) {
    held = cached;
    return cached;
  }

  throw new CoefficientsUnavailable();
}

/** The version the client is running, for analytics and result submission. */
export function heldVersion(): number {
  return held?.version ?? CURRENT_COEFFICIENT_VERSION;
}
